import { useState, useEffect } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Heading, Subheading } from '../components_catalyst/heading';
import { Text } from '../components_catalyst/text';
import { Link } from '../components_catalyst/link';
import { Input, InputGroup } from '../components_catalyst/input';
import clsx from 'clsx';

function StatusPreview({ className, label }: { className: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded ${className}`} />
      <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
    </div>
  );
}

function ThemePreview({ mode }: { mode: 'default' | 'colorblind' | 'grayscale' }) {
  const previewLink =
    mode === 'default'
      ? 'https://davidmathlogic.com/colorblind/#%23a0d76d-%23259950-%23fff28c-%23facd26-%23d12f2b-%23a50026-%23d9d9d9-%23b7b7b7-%23b7b7b7-%23f0f0f0'
      : mode === 'colorblind'
        ? 'https://davidmathlogic.com/colorblind/#%2300A77D-%23025A43-%23E8DC3C-%23C4BA2A-%23D95F04-%238C3D05-%23C5C4C2-%23B7B7B7-%23989898-%23EAEAEA'
        : 'https://davidmathlogic.com/colorblind/#%23efefef-%23efefef-%23cccccc-%23cccccc-%23000000-%23000000-%23ffffff-%23ffffff-%23ffffff-%23ffffff';

  const themeName = mode.charAt(0).toUpperCase() + mode.slice(1);

  return (
    <div className="mt-3 space-y-4">
      <div data-theme={mode}>
        <Subheading
          level={2}
          className="font-medium mb-2"
          aria-label={`${themeName} theme status colors`}
        >
          Status Colors:
        </Subheading>
        <div className="space-y-2">
          <StatusPreview className="bg-status-g" label="On Track (g)" />
          <StatusPreview className="bg-status-G" label="Complete Milestone (G)" />
          <StatusPreview className="bg-status-y" label="At Risk (y)" />
          <StatusPreview className="bg-status-Y" label="At Risk Milestone (Y)" />
          <StatusPreview className="bg-status-r" label="Behind (r)" />
          <StatusPreview className="bg-status-R" label="Behind Milestone (R)" />
          <StatusPreview className="bg-status-p" label="Planned (p)" />
          <StatusPreview className="bg-status-i" label="Inchstone (i)" />
          <StatusPreview className="bg-status-M" label="Milestone (M)" />
          <StatusPreview className="bg-status-tbd" label="To Be Determined (?)" />
        </div>
      </div>

      <div className="text-sm">
        <a
          href={previewLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
        >
          View how this palette appears with different types of color blindness →
        </a>
      </div>

      <div>
        <Subheading
          level={2}
          className="font-medium mb-2"
          aria-label={`${themeName} theme KPI scale preview`}
        >
          KPI Scale Preview:
        </Subheading>
        <div data-theme={mode}>
          <div className="flex h-6 w-full rounded overflow-hidden">
            {/* Red scale */}
            <div className="bg-status-r1 flex-1" title="Red 1" />
            <div className="bg-status-r2 flex-1" title="Red 2" />
            <div className="bg-status-r3 flex-1" title="Red 3" />
            <div className="bg-status-r4 flex-1" title="Red 4" />
            <div className="bg-status-r5 flex-1" title="Red 5" />
            <div className="bg-status-r6 flex-1" title="Red 6" />

            {/* Yellow scale */}
            <div className="bg-status-y7 flex-1" title="Yellow 7" />
            <div className="bg-status-y8 flex-1" title="Yellow 8" />
            <div className="bg-status-y9 flex-1" title="Yellow 9" />
            <div className="bg-status-y10 flex-1" title="Yellow 10" />
            <div className="bg-status-y11 flex-1" title="Yellow 11" />
            <div className="bg-status-y12 flex-1" title="Yellow 12" />

            {/* Green scale */}
            <div className="bg-status-g13 flex-1" title="Green 13" />
            <div className="bg-status-g14 flex-1" title="Green 14" />
            <div className="bg-status-g15 flex-1" title="Green 15" />
            <div className="bg-status-g16 flex-1" title="Green 16" />
            <div className="bg-status-g17 flex-1" title="Green 17" />
            <div className="bg-status-g18 flex-1" title="Green 18" />
            <div className="bg-status-g19 flex-1" title="Green 19" />
            <div className="bg-status-g20 flex-1" title="Green 20" />
            <div className="bg-status-g21 flex-1" title="Green 21" />
            <div className="bg-status-g22 flex-1" title="Green 22" />
          </div>
        </div>
      </div>

      <div className="text-sm">
        <a
          href={
            mode === 'default'
              ? 'https://davidmathlogic.com/colorblind/#%23a50026-%23bc1727-%23d12f2b-%23e24a33-%23ee6640-%23f6844e-%23fba15d-%23fdbc6e-%23fed382-%23fee698-%23fcf3aa-%23f5f8ac-%23e6f49d-%23d2ec89-%23bae279-%23a0d76d-%2384ca66-%2364bc61-%2343ab5a-%23259950-%23118446-%23036e3a'
              : mode === 'colorblind'
                ? 'https://davidmathlogic.com/colorblind/#%238C3D05-%23A34B06-%23B95A07-%23D95F04-%23E56E05-%23F27D06-%23D6CE35-%23E8DC3C-%23EDE258-%23F1E873-%23F5ED8F-%23F9F3AA-%23007F5F-%2300896A-%23009374-%2300A77D-%2300B588-%2300C392-%2300D19D-%2300DFA8-%2300EDB2-%2300FBBD'
                : 'https://davidmathlogic.com/colorblind/#%23000000-%230d0d0d-%231a1a1a-%23262626-%23333333-%23404040-%234d4d4d-%23595959-%23666666-%23737373-%23808080-%238c8c8c-%23999999-%23a6a6a6-%23b3b3b3-%23bfbfbf-%23cccccc-%23d9d9d9-%23e6e6e6-%23efefef-%23f7f7f7-%23ffffff'
          }
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
        >
          View how this KPI scale appears with different types of color blindness →
        </a>
      </div>
    </div>
  );
}

export function ThemesPage() {
  useDocumentTitle('Theme Settings');
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [mapUrl, setMapUrl] = useState('');
  const [themeUrl, setThemeUrl] = useState('');
  const [urlStatus, setUrlStatus] = useState<'empty' | 'invalid' | 'valid' | null>(null);
  const [urlTouched, setUrlTouched] = useState(false);

  const themes = [
    { value: 'default', label: 'Default Theme' },
    { value: 'colorblind', label: 'Colorblind-Friendly Theme' },
    { value: 'grayscale', label: 'Grayscale Theme' },
  ];

  const validateAndUpdateUrl = (url: string) => {
    if (!url.trim()) {
      setUrlStatus('empty');
      setThemeUrl('');
      return;
    }

    // Extract the path portion from various URL formats
    let path = url.trim();

    // Handle full URLs
    try {
      if (path.includes('://')) {
        const urlObj = new URL(path);
        if (!urlObj.hostname.endsWith('milestonemap.org')) {
          setUrlStatus('invalid');
          setThemeUrl('');
          return;
        }
        path = urlObj.pathname;
      } else if (path.includes('milestonemap.org/')) {
        path = path.split('milestonemap.org/')[1];
      }
    } catch (e) {
      setUrlStatus('invalid');
      setThemeUrl('');
      return;
    }

    // Remove leading slash if present
    path = path.replace(/^\//, '');

    // Validate path format
    const pathPattern = /^([^/]+)\/maps\/([^/?]+)(?:\?.*)?$/;
    const match = path.match(pathPattern);

    if (match) {
      const [, userName, mapShortId] = match;
      setUrlStatus('valid');

      // Determine base URL based on current environment
      const baseUrl = window.location.hostname.includes('stage')
        ? 'https://stage.milestonemap.org'
        : 'https://milestonemap.org';

      setThemeUrl(`${baseUrl}/${userName}/maps/${mapShortId}?theme=${selectedTheme}`);
    } else {
      setUrlStatus('invalid');
      setThemeUrl('');
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    if (!newUrl.trim()) {
      setUrlStatus(null);
      setUrlTouched(false);
    } else {
      setUrlTouched(true);
      validateAndUpdateUrl(newUrl);
    }
    setMapUrl(newUrl);
  };

  const showError = urlTouched && urlStatus === 'invalid';

  // Handle theme selection changes
  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTheme = e.target.value;
    setSelectedTheme(newTheme);
    validateAndUpdateUrl(mapUrl);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="space-y-4 mb-12">
        <Heading level={1}>Theme Settings</Heading>
        <Text className="text-base sm:text-lg">
          Try out different milestone map themes. Select whatever color palette you prefer then
          follow the link below. We're still figuring out how many color palettes to offer, which to
          suggest to whom, and how best to integrate this into the MilestoneMap.org experience.
        </Text>
        <Text className="text-base sm:text-lg">
          If you have suggestions, we'd love to hear from you! Please share them with us{' '}
          <Link
            href="https://forms.gle/faWbbpGAB1qnY7mh8"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            here
          </Link>
          .
        </Text>
      </div>

      <div className="mb-8">
        <label
          htmlFor="mapUrl"
          className="block text-base sm:text-lg font-medium text-gray-700 dark:text-gray-200 mb-2"
          id="mapUrl-label"
        >
          Map URL
        </label>
        <div className="relative block w-full">
          <Input
            type="text"
            id="mapUrl"
            name="mapUrl"
            value={mapUrl}
            onChange={handleUrlChange}
            onBlur={() => {
              if (mapUrl.trim()) {
                setUrlTouched(true);
              }
            }}
            placeholder="milestonemap.org/userName/maps/mapId"
            aria-labelledby="mapUrl-label"
            aria-describedby="mapUrl-description mapUrl-error"
            data-invalid={showError ? '' : undefined}
          />
        </div>
        <Text id="mapUrl-description" className="mt-1 text-base sm:text-lg text-gray-500">
          Example: milestonemap.org/JaneSmith/maps/abc123
        </Text>
        {showError && (
          <Text
            id="mapUrl-error"
            className="mt-2 text-base sm:text-lg text-red-600 dark:text-red-400"
            role="alert"
          >
            Please enter a valid milestone map URL
          </Text>
        )}
        {urlStatus === 'valid' && (
          <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md">
            <Text className="text-base sm:text-lg text-blue-700 dark:text-blue-300">
              Try out the {selectedTheme} theme:{' '}
              <Link
                href={themeUrl}
                className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
              >
                {themeUrl}
              </Link>
            </Text>
          </div>
        )}
      </div>

      <div className="space-y-6" role="radiogroup" aria-label="Theme selection">
        {themes.map((theme) => (
          <div key={theme.value} className="p-4 border rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="radio"
                  id={`theme-${theme.value}`}
                  name="theme"
                  value={theme.value}
                  checked={selectedTheme === theme.value}
                  onChange={handleThemeChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  aria-describedby={`theme-${theme.value}-description`}
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor={`theme-${theme.value}`}
                  className="block text-base sm:text-lg font-medium text-gray-700 dark:text-gray-200"
                >
                  {theme.label}
                </label>
                <div id={`theme-${theme.value}-description`}>
                  <ThemePreview mode={theme.value as 'default' | 'colorblind' | 'grayscale'} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
